import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { TbCalendar, TbFolder } from 'react-icons/tb';
import logoUrl from '../../../assets/icons/logo-new.png';
import Profile from '../Sidebar/Profile';
import { useWorkflowChrome } from '../WorkflowChromeContext';
// @ts-expect-error untyped JS service
import {
  useGetApproverWorkflows,
  useGetWorkflows,
  useGetWorkflowById
} from '../../../services/workflow.services';
// @ts-expect-error untyped JS service
import {
  useUserGroups,
  useUpdateUserCompany,
  useUserCompaniesCurrentUserAdminState
} from '../../../services/user.service';
// @ts-expect-error untyped JS store
import authStore from '../../../store/auth';
// @ts-expect-error untyped JS module
import { queryClient } from '../../../config/queryClient';
// @ts-expect-error untyped JS const
import { GET_WORKFLOW } from '../../../consts/queryKeys';
// @ts-expect-error untyped JS const
import { QUERY_KEYS } from '../../../services/constants';
import toast from 'react-hot-toast';
import { CompanyBreadcrumb, ProjectBreadcrumb, PeriodBreadcrumb } from './breadcrumbs';
// @ts-expect-error untyped JS util
import { checkToRedirect } from '../../../utils/checkToRedirect';
// @ts-expect-error untyped JS util
import {
  getStageFromLocation,
  pickPeriodSwitchDestination,
  WORKFLOW_STAGE_KEYS
} from '../../../utils/workflowNavigation';
// @ts-expect-error untyped JS service
import { jurisdictionService } from '../../../services/jurisdiction.service';
// @ts-expect-error untyped JS const
import { REPORT_TYPES } from '../../../consts/reportTypes';
import { TopBar, BreadcrumbGroup, Separator, RightSlot, ProfileSlot, Chip } from './style';

export interface AppTopBarProps {
  /** Override company label (otherwise read from context). */
  companyLabel?: string;
  /** Override project label (otherwise read from context). */
  projectLabel?: string;
  /** Override period label (otherwise read from context). */
  periodLabel?: string;
  /** Override visibility of the company breadcrumb. */
  showCompany?: boolean;
  /** Override visibility of the comments icon. */
  showComments?: boolean;
  /** Override comments icon click handler. */
  onCommentsClick?: () => void;
}

const AppTopBar: React.FC<AppTopBarProps> = props => {
  const ctx = useWorkflowChrome();
  const navigate = useNavigate();
  const location = useLocation();
  const activeCompanyIdRef = useRef<string | null>(localStorage.getItem('activeCompanyId'));

  // Datox-internal users (email ending with @datox.ai) can switch the active
  // company — everyone else is implicitly scoped to their own company and
  // doesn't see the chip at all.
  const userEmail = (authStore?.user?.username as string | undefined) ?? '';
  const isDatoxUser = userEmail.toLowerCase().endsWith('@datox.ai');

  const { data: userGroups, refetch: refetchUserCompanies } = useUserGroups({
    queryOptions: { enabled: isDatoxUser }
  }) as { data?: any; refetch: () => void };
  const updateUserCompany = useUpdateUserCompany();

  const { data: currentUserState } = useUserCompaniesCurrentUserAdminState();
  const isApprover = currentUserState?.role === 'Approver';

  const companyOptions = useMemo(() => {
    // Runtime shape is `{ company_id, group_name }[]` — Sidebar/Profile/Menu
    // reads the same `group_name` field. (`company_name` does not exist in
    // the response; using it left the label empty and fell back to "Company".)
    const groups = (userGroups?.user_groups as Array<any>) ?? [];
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return groups
      .map(c => ({
        value: c?.company_id as string,
        label: (c?.group_name ?? c?.company_name) as string
      }))
      .filter(
        opt => opt.value && opt.label && opt.label.trim() !== '' && !UUID_RE.test(opt.label.trim())
      );
  }, [userGroups]);

  const currentCompanyId = userGroups?.current_company_id as string | undefined;
  const currentCompanyName = useMemo(() => {
    if (!currentCompanyId) return undefined;
    const found = companyOptions.find(c => c.value === currentCompanyId);
    return found?.label;
  }, [companyOptions, currentCompanyId]);

  const handleSelectCompany = (companyId: string) => {
    if (companyId === currentCompanyId) {
      // Clicking the already-active company is treated as "back to project
      // list" — useful for users deep inside a workflow who want to exit.
      navigate('/project');
      return;
    }
    updateUserCompany.mutate(
      { data: { company_id: companyId } },
      {
        onSuccess: () => {
          // The API request interceptor reads activeCompanyId from
          // localStorage to set the X-Active-Company-Id header — same
          // pattern as the legacy Profile menu's company switcher.
          activeCompanyIdRef.current = companyId;
          localStorage.setItem('activeCompanyId', companyId);
          toast.success('The company has been successfully switched');
          authStore.setWorkflow(null, '');
          queryClient.removeQueries(GET_WORKFLOW);
          queryClient.removeQueries('WORKFLOW_ACCESS_GUARD');
          queryClient.removeQueries(QUERY_KEYS.GET_USER_COMPANIES_ALL_USERS);
          queryClient.invalidateQueries();
          queryClient.refetchQueries(QUERY_KEYS.GET_USER_COMPANIES_CURRENT_USER_ADMIN_STATE, {
            active: true
          });
          refetchUserCompanies();
          // Send the user back to the projects list — the previous
          // workflow/period IDs belong to the old company and would 404.
          navigate('/project');
        },
        onError: (err: any) => {
          toast.error(`Update company ${err?.data?.detail ?? 'failed'}`);
        }
      }
    );
  };

  useEffect(() => {
    const handleActiveCompanyStorageChange = (event: StorageEvent) => {
      if (event.key !== 'activeCompanyId') return;
      if (event.newValue === activeCompanyIdRef.current) return;

      activeCompanyIdRef.current = event.newValue;
      authStore.setWorkflow(null, '');
      queryClient.clear();
      window.location.replace('/project');
    };

    window.addEventListener('storage', handleActiveCompanyStorageChange);

    return () => {
      window.removeEventListener('storage', handleActiveCompanyStorageChange);
    };
  }, []);

  const companyLabel = props.companyLabel ?? ctx.companyLabel ?? currentCompanyName;
  const projectLabel = props.projectLabel ?? ctx.projectLabel;
  const periodLabel = props.periodLabel ?? ctx.periodLabel;
  const showCompany = props.showCompany ?? ctx.showCompany ?? isDatoxUser;
  const hasProject = !!projectLabel;
  const hasPeriod = !!periodLabel;

  // Project search: debounce typing so each keystroke doesn't fire a request.
  const [projectSearch, setProjectSearch] = useState('');
  const [debouncedProjectSearch, setDebouncedProjectSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedProjectSearch(projectSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [projectSearch]);

  // Project list — server-side search via the paginated workflows endpoint.
  // Cap to 12 so the dropdown stays a manageable height; users find anything
  // beyond that by typing in the search input (forwards to the API).
  const { workflows: projectWorkflows } = useGetWorkflows({
    pageSize: 12,
    search: debouncedProjectSearch,
    queryParams: { enabled: hasProject && !isApprover, keepPreviousData: true }
  }) as { workflows: any[] };

  // Periods of the current workflow — pages already fetch this, so this is a cache hit.
  const { data: currentWorkflow } = useGetWorkflowById({
    id: ctx.workflowId,
    queryParams: { enabled: !!ctx.workflowId && hasPeriod }
  }) as { data?: any };

  const { data: approverWorkflows } = useGetApproverWorkflows({
    queryParams: { enabled: isApprover && hasPeriod }
  }) as { data?: any[] };

  const projectOptions = useMemo(() => {
    const items = (projectWorkflows as any[]) ?? [];
    return items.filter(w => w?.id).map(w => ({ label: w.name as string, value: w.id as string }));
  }, [projectWorkflows]);

  // Index the workflow rows by id so we can route via `checkToRedirect`
  // (single-period projects → Funds, grouped/multi-period → Quarters).
  const workflowById = useMemo(() => {
    const items = (projectWorkflows as any[]) ?? [];
    return items.reduce<Record<string, any>>((acc, w) => {
      if (w?.id) acc[w.id] = w;
      return acc;
    }, {});
  }, [projectWorkflows]);

  const periodOptions = useMemo(() => {
    const periods = (currentWorkflow?.periods as any[]) ?? [];
    return periods
      .filter(p => p?.id)
      .map(p => ({
        label: p.year && p.period ? `${p.year}, ${p.period}` : (p.id as string),
        value: p.id as string
      }));
  }, [currentWorkflow]);

  const approverPeriodOptions = useMemo(() => {
    const seen = new Set<string>();
    return ((approverWorkflows as any[]) ?? [])
      .filter(item => item?.workflow_id === ctx.workflowId && item?.period_id)
      .filter(item => {
        if (seen.has(item.period_id)) return false;
        seen.add(item.period_id);
        return true;
      })
      .map(item => ({
        label: item.year && item.period ? `${item.year}, ${item.period}` : item.period_id,
        value: item.period_id
      }));
  }, [approverWorkflows, ctx.workflowId]);

  const approverProjectOptions = useMemo(() => {
    const seen = new Set<string>();
    return ((approverWorkflows as any[]) ?? [])
      .filter(item => item?.workflow_id && item?.workflow_name)
      .filter(item => {
        if (seen.has(item.workflow_id)) return false;
        seen.add(item.workflow_id);
        return true;
      })
      .map(item => ({
        label: item.workflow_name,
        value: item.workflow_id
      }));
  }, [approverWorkflows]);

  const handleSelectProject = (workflowId: string) => {
    // Route via `checkToRedirect` so the destination matches the workflow's
    // shape — Conversion / grouped / multi-period workflows land on the
    // Quarters page, single-period workflows jump straight to Funds, and
    // child workflows hop to their group's grouped view. Falls back to the
    // Quarters page if the row is missing or doesn't resolve.
    const workflow = workflowById[workflowId];
    const target = checkToRedirect(workflow);
    if (target) {
      navigate(target);
      return;
    }
    const groupedSuffix = workflow?.is_workflow_group ? '?type=grouped' : '';
    navigate(`/project/quaters/${workflowId}${groupedSuffix}`);
  };

  const handleSelectPeriod = async (newPeriodId: string) => {
    if (!ctx.periodId || newPeriodId === ctx.periodId) return;
    const wfId = ctx.workflowId;
    if (!wfId) return;

    const fallback = `/project/quaters/${wfId}/${newPeriodId}/add-aifs`;
    const destPeriod = ((currentWorkflow?.periods as any[]) ?? []).find(p => p?.id === newPeriodId);
    if (!destPeriod) {
      navigate(fallback);
      return;
    }

    // Where is the user right now? If they're on a specific stage page
    // (Report / Output Tables / Extraction / etc.) we try to take them to
    // the same stage in the destination period; otherwise we fall back to
    // AddAIFs. URL state (fileId/sheetId/tab selections) is intentionally
    // dropped — those values don't carry across periods.
    const requestedStage = getStageFromLocation(location.pathname, location.search);

    // Report / Approval / Submission URLs include the destination's FCA vs
    // ESMA segment, which depends on the first jurisdiction's
    // reporting_member_state. Form PF + Conversion-with-no-jurisdictions
    // skip the fetch — the helper handles both paths internally.
    const reportFamily =
      requestedStage === WORKFLOW_STAGE_KEYS.REPORT ||
      requestedStage === WORKFLOW_STAGE_KEYS.APPROVAL ||
      requestedStage === WORKFLOW_STAGE_KEYS.SUBMISSION;
    const needsJurisdictions =
      reportFamily && currentWorkflow?.report_type !== REPORT_TYPES.FORM_PF;
    let destJurisdictions: any[] | undefined;
    if (needsJurisdictions) {
      try {
        destJurisdictions = await queryClient.fetchQuery(
          ['GET_JURISDICTIONS', newPeriodId, {}],
          () => jurisdictionService.getJurisdictions(newPeriodId, {})
        );
      } catch {
        destJurisdictions = undefined;
      }
    }

    const dest = pickPeriodSwitchDestination({
      requestedStage,
      workflowId: wfId,
      newPeriodId,
      destPeriod,
      project: currentWorkflow,
      destJurisdictions
    });
    navigate(dest || fallback);
  };

  const handleSelectApproverPeriod = async (newPeriodId: string) => {
    if (!ctx.workflowId || newPeriodId === ctx.periodId) return;

    const record = ((approverWorkflows as any[]) ?? []).find(
      item => item?.workflow_id === ctx.workflowId && item?.period_id === newPeriodId
    );
    if (!record) return;

    await navigateToApproverRecord(record);
  };

  const handleSelectApproverProject = async (workflowId: string) => {
    if (workflowId === ctx.workflowId) return;

    const record = ((approverWorkflows as any[]) ?? []).find(
      item => item?.workflow_id === workflowId
    );
    if (!record) return;

    await navigateToApproverRecord(record);
  };

  const navigateToApproverRecord = async (record: any) => {
    if (record.workflow_type === 'Form PF') {
      navigate(`/report/pf/us/${record.workflow_id}/${record.period_id}`);
      return;
    }

    try {
      const res: any = await jurisdictionService.getJurisdictionsApprover(record.period_id);
      const jurisdiction = res?.[0];
      if (!jurisdiction) return;

      const reportingMemberState = jurisdiction.reporting_member_state;
      const isFCA = reportingMemberState === 'UK' || reportingMemberState === 'uk';
      const isAifmOnly = record.is_aifm_included && !record.status?.length;
      const groupedQuery = isAifmOnly ? '?type=AIF001' : '';

      if (isFCA)
        navigate(`/report/fca/uk/${record.workflow_id}/${record.period_id}${groupedQuery}`);
      else {
        navigate(
          `/report/esma/${reportingMemberState?.toLowerCase()}/${record.workflow_id}/${record.period_id}${groupedQuery}`
        );
      }
    } catch {
      navigate('/project');
    }
  };

  const handleAddProject = () => {
    navigate('/project?createProject=1');
  };

  return (
    <TopBar>
      <NavLink
        id="datox-home-logo-link"
        className="logo-link"
        to="/project"
        aria-label="Datox home"
      >
        <img id="datox-home-logo" className="logo" src={logoUrl} alt="Datox" />
      </NavLink>

      <BreadcrumbGroup>
        {showCompany && (
          <CompanyBreadcrumb
            label={companyLabel ?? 'Company'}
            options={companyOptions}
            onSelect={handleSelectCompany}
            activeValue={currentCompanyId}
          />
        )}
        {showCompany && hasProject && <Separator>/</Separator>}
        {hasProject && !isApprover && (
          <ProjectBreadcrumb
            label={projectLabel as string}
            options={projectOptions}
            onSelect={handleSelectProject}
            footerAction={{ label: 'Add Project', onClick: handleAddProject }}
            activeValue={ctx.workflowId}
            searchValue={projectSearch}
            onSearchChange={setProjectSearch}
          />
        )}
        {hasProject && isApprover && approverProjectOptions.length > 1 && (
          <ProjectBreadcrumb
            label={projectLabel as string}
            options={approverProjectOptions}
            onSelect={handleSelectApproverProject}
            activeValue={ctx.workflowId}
          />
        )}
        {hasProject && isApprover && approverProjectOptions.length <= 1 && (
          <Chip type="button" disabled>
            <TbFolder size={16} />
            <span className="label">{projectLabel}</span>
          </Chip>
        )}
        {hasProject && hasPeriod && <Separator>/</Separator>}
        {hasPeriod && !isApprover && (
          <PeriodBreadcrumb
            label={periodLabel as string}
            options={periodOptions}
            onSelect={handleSelectPeriod}
            activeValue={ctx.periodId}
          />
        )}
        {hasPeriod && isApprover && approverPeriodOptions.length > 1 && (
          <PeriodBreadcrumb
            label={periodLabel as string}
            options={approverPeriodOptions}
            onSelect={handleSelectApproverPeriod}
            activeValue={ctx.periodId}
          />
        )}
        {hasPeriod && isApprover && approverPeriodOptions.length <= 1 && (
          <Chip type="button" disabled>
            <TbCalendar size={16} />
            <span className="label">{periodLabel}</span>
          </Chip>
        )}
      </BreadcrumbGroup>

      <RightSlot>
        <ProfileSlot>
          <Profile open={false} avatarSize="24px" />
        </ProfileSlot>
      </RightSlot>
    </TopBar>
  );
};

export default AppTopBar;
